<template>
  <div v-if="fname?.length">
    <template v-if="blok.asset?.title">
      <figure class="asset-image-with-caption">
        <nuxt-img
          :src="fname"
          :width="dimensions[0]"
          :height="dimensions[1]"
          :sizes="resolvedSizes"
          :quality="quality"
          :preload="imagePreload"
          :loading="imageLoading"
          :fetchpriority="resolvedFetchPriority"
          :alt="blok.asset?.alt"
          provider="storyblok"
        />
          <figcaption>{{ blok.asset?.title }}</figcaption>
      </figure>
    </template>
    <template v-else>
      <template v-if="!blok.asset?.is_external_url && !isSvg">
        <nuxt-img
          :src="fname"
          :width="dimensions[0]"
          :height="dimensions[1]"
          :sizes="resolvedSizes"
          :quality="quality"
          :preload="imagePreload"
          :loading="imageLoading"
          :fetchpriority="resolvedFetchPriority"
          :alt="blok.asset?.alt"
          class="asset-image"
          provider="storyblok"
        />
      </template>
      <template v-else-if="isSvg && svgForceDimensions">
        <img
          :src="fname"
          :loading="imageLoading"
          :fetchpriority="resolvedFetchPriority"
          :alt="blok.asset?.alt"
          :width="dimensions[0]"
          :height="dimensions[1]"
          class="asset-image"
        />
      </template>
      <template v-else>
        <img
          :src="fname"
          :loading="imageLoading"
          :fetchpriority="resolvedFetchPriority"
          :alt="blok.asset?.alt"
          class="asset-image"
        />
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  preset: {
    type: String,
    default: "default",
  },
  sizeHint: {
    type: Number,
    required: false,
    default: 100,
  },
  sizeHintMobile: {
    type: Number,
    required: false,
    default: undefined,
  },
  svgForceDimensions: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: String,
    required: false,
    default: undefined,
  },
  fetchPriority: {
    type: String,
    required: false,
    default: undefined,
  },
  maxWidth: {
    type: Number,
    required: false,
    default: undefined,
  },
  quality: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const imageLoading = computed(() => {
  if (props.loading) return props.loading;
  return props.blok.lazy ? "lazy" : "eager";
});

const shouldPreload = computed(() => props.fetchPriority === "high");

const resolvedFetchPriority = computed(() => {
  if (props.fetchPriority === "high") return "high";
  if (props.fetchPriority === "low" || props.fetchPriority === "auto") {
    return props.fetchPriority;
  }
  return undefined;
});

// Always emit phone + desktop so Nuxt Image builds a w-srcset for both.
// maxWidth → fixed px at each breakpoint (densities 1x/2x = Retina without oversize).
// otherwise → vw hints (mobile defaults to 100vw unless sizeHintMobile is set).
const resolvedSizes = computed(() => {
  if (props.maxWidth) {
    return `phone:${props.maxWidth}px desktop:${props.maxWidth}px`;
  }
  const mobileHint = props.sizeHintMobile ?? 100;
  return `phone:${mobileHint}vw desktop:${props.sizeHint}vw`;
});

const imagePreload = computed(() =>
  shouldPreload.value ? { fetchPriority: "high" } : undefined
);

const dimensions = computed(() => {
  const dxd = props.blok.asset?.filename?.split("/")
    .filter((el) => el.match(/\d+[xX]\d+/))[0];

  if (!dxd) {
    if (props.maxWidth) {
      return [props.maxWidth.toString(), props.maxWidth.toString()];
    }
    return "";
  }

  const rawDimensions = dxd?.split(/[xX]/);
  const sourceWidth = Number(rawDimensions[0]);
  const sourceHeight = Number(rawDimensions[1]);
  const ratio = sourceWidth ? sourceHeight / sourceWidth : 1;

  // Cap only — never pick mobile XOR desktop here; sizes drives the srcset.
  let width = sourceWidth;
  if (props.maxWidth && width > props.maxWidth) {
    width = props.maxWidth;
  }
  const height = Math.round(width * ratio);

  return [width.toString(), height.toString()];
});

const aspectRatio = computed(() => {
  return `${dimensions.value[0]}/${dimensions.value[1]}`;
});

const fname = computed(() => {
  return props.blok?.asset?.filename?.replace(/s3\.amazonaws\.com\//, "");
});

const isSvg = computed(() => {
  return /\.svg$/.test(props.blok?.asset?.filename);
});
</script>

<style lang="scss" scoped>
img {
  max-inline-size: 100%;
  block-size: auto;
  aspect-ratio: v-bind("aspectRatio");
  object-fit: cover;
  object-position: center center;
}
// With border-radius
.news-content-inner {
  img {
    border-radius: $radius-l;
  }
}
</style>
